import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, X, Headphones, Bookmark, GraduationCap } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { api } from "../lib/api";
import AudioPlayer from "../components/AudioPlayer";
import HighlightPopover from "../components/HighlightPopover";
import StudyRecap from "../components/StudyRecap";
import {
  ChaptersButton,
  BookmarksButton,
  BookNotesButton,
  ReaderSettingsButton,
  SearchInsideButton,
} from "../components/ReaderDrawer";
import { useAuth } from "../context/AuthContext";
import { useReaderSettings } from "../hooks/useReaderSettings";
import { toast } from "sonner";

export default function Reader() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { user } = useAuth();
  const [book, setBook] = useState(null);
  const [pages, setPages] = useState([]);
  const [chapters, setChapters] = useState([{ title: "Beginning", page: 0 }]);
  const [page, setPage] = useState(parseInt(sp.get("p") || "0", 10));
  const [dir, setDir] = useState(1);
  const [loading, setLoading] = useState(true);
  const [audioOn, setAudioOn] = useState(sp.get("audio") === "1");
  const [studyMode, setStudyMode] = useState(sp.get("study") === "1");
  const [recapOpen, setRecapOpen] = useState(false);
  const [recapChapterIdx, setRecapChapterIdx] = useState(0);
  const [selection, setSelection] = useState(null);
  const [settings, setSettings] = useReaderSettings();
  const articleRef = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [b, c] = await Promise.all([
          api.get(`/books/${id}`),
          api.get(`/books/${id}/content`),
        ]);
        setBook(b.data);
        setPages(c.data.pages);
        setChapters(c.data.chapters || [{ title: "Beginning", page: 0 }]);
        if (user) {
          try {
            const p = await api.get(`/progress/${id}`);
            if (p.data.progress?.page != null && !sp.get("p")) {
              setPage(p.data.progress.page);
            }
          } catch { /* noop */ }
        }
      } catch {
        toast.error("could not open this volume");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // persist progress
  useEffect(() => {
    if (!user || !pages.length) return;
    const t = setTimeout(() => {
      api.post("/progress", { book_id: id, page }).catch(() => {});
    }, 700);
    return () => clearTimeout(t);
  }, [page, id, user, pages.length]);

  // reading-time heartbeat for goals & stats
  useEffect(() => {
    if (!user) return;
    const t = setInterval(() => {
      if (document.visibilityState === "visible") api.post("/reading/tick", { seconds: 30 }).catch(() => {});
    }, 30000);
    return () => clearInterval(t);
  }, [user]);

  // update url
  useEffect(() => {
    const params = {};
    if (page > 0) params.p = String(page);
    if (audioOn) params.audio = "1";
    if (studyMode) params.study = "1";
    setSp(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, audioOn, studyMode]);

  const goto = useCallback((next) => {
    if (next < 0 || next >= pages.length) return;
    // In study mode: when leaving a chapter (crossing its last page forward),
    // pop up the recap for the chapter we just finished.
    if (studyMode && next > page && chapters.length > 0) {
      const currentIdx = chapters.reduce((acc, c, i) => (c.page <= page ? i : acc), 0);
      const nextChapterStart = chapters[currentIdx + 1]?.page ?? pages.length;
      const isLastOfChapter = page === nextChapterStart - 1;
      if (isLastOfChapter) {
        setRecapChapterIdx(currentIdx);
        setRecapOpen(true);
        return; // pause the page turn — recap first
      }
    }
    setDir(next > page ? 1 : -1);
    setPage(next);
    setSelection(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [page, pages.length, studyMode, chapters]);

  const openRecapForCurrentChapter = () => {
    if (!chapters.length) return;
    const idx = chapters.reduce((acc, c, i) => (c.page <= page ? i : acc), 0);
    setRecapChapterIdx(idx);
    setRecapOpen(true);
  };

  const advanceAfterRecap = () => {
    setRecapOpen(false);
    const nextChapter = chapters[recapChapterIdx + 1];
    if (nextChapter) {
      setDir(1);
      setPage(nextChapter.page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setDir(1);
      setPage(Math.min(page + 1, pages.length - 1));
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.target && ["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.key === "ArrowRight") goto(page + 1);
      if (e.key === "ArrowLeft") goto(page - 1);
      if (e.key === "Escape") navigate(`/book/${id}`);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goto, page, id, navigate]);

  // Highlight capture on text selection
  useEffect(() => {
    const handler = () => {
      const s = window.getSelection();
      if (!s || s.isCollapsed) { setSelection(null); return; }
      const text = s.toString().trim();
      if (text.length < 4) { setSelection(null); return; }
      const range = s.getRangeAt(0);
      // Only capture selections inside the article
      if (!articleRef.current || !articleRef.current.contains(range.commonAncestorContainer)) {
        setSelection(null);
        return;
      }
      const rect = range.getBoundingClientRect();
      setSelection({ text, rect });
    };
    document.addEventListener("mouseup", handler);
    document.addEventListener("touchend", handler);
    return () => {
      document.removeEventListener("mouseup", handler);
      document.removeEventListener("touchend", handler);
    };
  }, []);

  const bookmark = async () => {
    if (!user) { toast.error("sign in to bookmark"); return; }
    try {
      await api.post("/bookmarks", { book_id: id, page });
      toast.success("bookmarked.");
    } catch {
      toast.error("could not bookmark");
    }
  };

  if (loading) return <div className="max-w-3xl mx-auto px-6 py-32 text-center font-ui text-muted-foreground">turning to page one…</div>;
  if (!book) return null;

  const pageText = pages[page] || "";
  const paragraphs = pageText.split("\n\n");
  const pct = pages.length ? ((page + 1) / pages.length) * 100 : 0;
  const currentChapter = [...chapters].reverse().find((c) => c.page <= page);

  const variants = {
    enter: (d) => ({ opacity: 0, x: d > 0 ? 40 : -40 }),
    center: { opacity: 1, x: 0 },
    exit: (d) => ({ opacity: 0, x: d > 0 ? -40 : 40 }),
  };

  return (
    <div className="min-h-screen relative">
      {/* Top chrome */}
      <div className={`glass-header sticky top-0 z-30 border-b border-border ${studyMode ? "opacity-40 hover:opacity-100 transition-opacity" : ""}`}>
        <div className="max-w-5xl mx-auto flex items-center gap-2 px-4 md:px-8 h-14">
          <Link to={`/book/${id}`} className="font-ui text-xs tracking-caps text-muted-foreground hover:text-primary shrink-0" data-testid="reader-close-btn">
            <X className="inline w-3.5 h-3.5 mr-1" /> close
          </Link>
          <div className="flex-1 min-w-0 px-2">
            <div className="font-display text-base truncate">{book.title}</div>
            <div className="font-ui text-[10px] tracking-caps text-muted-foreground truncate">
              {book.author}{currentChapter && ` · ${currentChapter.title}`}
            </div>
          </div>

          {!studyMode && (
            <>
              <ChaptersButton chapters={chapters} currentPage={page} onJump={goto} />
              <SearchInsideButton bookId={id} onJump={goto} />
              <BookmarksButton bookId={id} currentPage={page} onJump={goto} />
              <BookNotesButton bookId={id} onJump={goto} />
              <ReaderSettingsButton settings={settings} setSettings={setSettings} />

              <button
                onClick={bookmark}
                data-testid="bookmark-btn"
                className="p-2 rounded-full hover:bg-secondary transition-colors"
                title="bookmark this page"
              >
                <Bookmark className="w-4 h-4" />
              </button>
              <button
                onClick={() => setAudioOn((v) => !v)}
                data-testid="toggle-audio-btn"
                className={`font-ui text-xs tracking-caps px-3 py-1.5 rounded-full border transition-colors ${
                  audioOn ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"
                }`}
              >
                <Headphones className="inline w-3.5 h-3.5 mr-1.5" /> <span className="hidden sm:inline">audiobook {audioOn ? "on" : "off"}</span>
              </button>
            </>
          )}

          {studyMode && (
            <button
              onClick={openRecapForCurrentChapter}
              data-testid="recap-chapter-btn"
              className="font-ui text-xs tracking-caps px-3 py-1.5 rounded-full border border-border hover:bg-secondary transition-colors"
              title="recap this chapter"
            >
              recap chapter
            </button>
          )}

          <button
            onClick={() => setStudyMode((v) => !v)}
            data-testid="toggle-study-btn"
            className={`font-ui text-xs tracking-caps px-3 py-1.5 rounded-full border transition-colors ml-1 ${
              studyMode ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"
            }`}
            title="study mode — locked-in reading with chapter recaps"
          >
            <GraduationCap className="inline w-3.5 h-3.5 mr-1.5" /> <span className="hidden sm:inline">study {studyMode ? "on" : "off"}</span>
          </button>
        </div>
        <div className="h-0.5 bg-muted">
          <div
            className="h-full bg-primary transition-[width] duration-500"
            style={{ width: `${pct}%` }}
            data-testid="reader-progress-bar"
          />
        </div>
      </div>

      {/* Page zones */}
      <div className="page-zone left" onClick={() => goto(page - 1)} data-testid="prev-page-zone" aria-label="previous page">
        <ChevronLeft className="zone-hint" />
      </div>
      <div className="page-zone right" onClick={() => goto(page + 1)} data-testid="next-page-zone" aria-label="next page">
        <ChevronRight className="zone-hint" />
      </div>

      {/* Page */}
      <div
        className="mx-auto px-6 md:px-14 pt-14 pb-40 relative z-10"
        style={{ maxWidth: settings.maxWidth }}
      >
        <AnimatePresence mode="wait" custom={dir}>
          <motion.article
            ref={articleRef}
            key={page}
            custom={dir}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.28, ease: [0.2, 0.7, 0.2, 1] }}
            className="reader-page space-y-6"
            data-testid="reader-page"
            style={{
              fontFamily: settings.fontFamily,
              fontSize: `${settings.fontSize}px`,
              lineHeight: settings.lineHeight,
            }}
          >
            {paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </motion.article>
        </AnimatePresence>

        <div className="mt-16 flex items-center justify-between font-ui text-xs tracking-caps text-muted-foreground">
          <button
            onClick={() => goto(page - 1)}
            disabled={page === 0}
            data-testid="prev-page-btn"
            className="disabled:opacity-30 hover:text-primary transition-colors"
          >
            <ChevronLeft className="inline w-3.5 h-3.5 mr-1" /> previous
          </button>
          <span data-testid="reader-page-indicator">
            page {page + 1} of {pages.length}
          </span>
          <button
            onClick={() => goto(page + 1)}
            disabled={page >= pages.length - 1}
            data-testid="next-page-btn"
            className="disabled:opacity-30 hover:text-primary transition-colors"
          >
            next <ChevronRight className="inline w-3.5 h-3.5 ml-1" />
          </button>
        </div>
      </div>

      {selection && (
        <HighlightPopover
          selection={selection}
          bookId={id}
          page={page}
          onClose={() => { setSelection(null); window.getSelection?.().removeAllRanges(); }}
          onSaved={() => setSelection(null)}
        />
      )}

      {audioOn && (
        <AudioPlayer
          pageText={pageText}
          pageIndex={page}
          onClose={() => setAudioOn(false)}
          onNextPage={() => goto(page + 1)}
        />
      )}

      <AnimatePresence>
        {recapOpen && (
          <StudyRecap
            bookId={id}
            chapterIndex={recapChapterIdx}
            chapterTitle={chapters[recapChapterIdx]?.title || "chapter recap"}
            onClose={() => setRecapOpen(false)}
            onNextChapter={chapters[recapChapterIdx + 1] ? advanceAfterRecap : null}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
